import type { SpinCoordinator } from "./SpinCoordinator";
import type { CanvasPoint, CanvasTapRegistry } from "./CanvasTapTargets";

/** The grid's tap surface, as an input binder (the view decides which node receives the taps): each tap's global point. */
export interface GridSlamInput {
  bindTap(onTap: (globalPoint: CanvasPoint) => void): () => void;
}

/**
 * Forwards taps on the grid's input surface: first to the title's canvas tap targets (decision 51), then, when none
 * took it, to the SpinCoordinator as a slam stop while it can slam.
 * The cue and the haptic come from `SpinMotionControl.slamStop` (the coordinator's `onSlamStop`), only
 * when the mechanic or a slam scope had motion to cut short.
 */
export class GridSlamTouchPresenter {
  private readonly gridInput: GridSlamInput;
  private unbind: (() => void) | null = null;
  private readonly spinCoordinator: SpinCoordinator;
  private readonly tapTargets: CanvasTapRegistry;

  private started = false;

  public constructor(input: { gridInput: GridSlamInput; spinCoordinator: SpinCoordinator; tapTargets: CanvasTapRegistry }) {
    this.gridInput = input.gridInput;
    this.spinCoordinator = input.spinCoordinator;
    this.tapTargets = input.tapTargets;
  }

  public start(): void {
    if (this.started) return;
    this.started = true;
    this.unbind = this.gridInput.bindTap((point) => this.handleGridTouch(point));
  }

  public stop(): void {
    if (!this.started) return;
    this.started = false;
    this.unbind?.();
    this.unbind = null;
  }

  private handleGridTouch(point: CanvasPoint): void {
    if (this.tapTargets.offer(point)) return;
    const snap = this.spinCoordinator.getSnapshot();
    if (!snap.canSlamStop) return;
    this.spinCoordinator.slamStop();
  }
}
